import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import BurialPlotBookingForm from '../../components/bookings/BurialPlotBookingForm';
import BankTransferPayment from '../../components/payments/BankTransferPayment';
import { casesService } from '../../services/cases.service';
import { burialPlotBookingService } from '../../services/burial-plot-booking.service';

interface BurialPlotBookingData {
  burialSize: 'ADULT' | 'CHILD';
  burialArea: string;
  preparationDate?: string;
  urgencyLevel: 'URGENT' | 'STANDARD';
  specialRequests?: string;
  areaCoordinates?: { lat: number; lng: number };
}

interface PricingResult {
  basePrice: number;
  urgencySurcharge: number;
  total: number;
  breakdown: string;
  currency?: string;
}

const BurialPlotBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [existingCases, setExistingCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentBooking, setCurrentBooking] = useState<any>(null);
  const [currentPricing, setCurrentPricing] = useState<PricingResult | null>(null);

  useEffect(() => {
    loadExistingCases();
  }, []);

  const loadExistingCases = async () => {
    try {
      const cases = await casesService.getCases();
      setExistingCases(cases || []);
    } catch (error) {
      console.error('Failed to load cases:', error);
      toast.error('Failed to load existing cases');
    }
  };

  const handleCaseSelect = (caseId: string) => {
    setSelectedCase(caseId);
    setShowBookingForm(true);
  };

  const handleNewBooking = () => {
    setSelectedCase(null);
    setShowBookingForm(true);
  };

  const handlePaymentSuccess = (paymentData: any) => {
    toast.success('Payment submitted successfully! Your burial plot booking has been confirmed.');
    setShowPaymentModal(false);
    setCurrentBooking(null);
    setCurrentPricing(null);

    // Navigate to case details to see the booking or to dashboard
    if (selectedCase) {
      navigate(`/cases/${selectedCase}`);
    } else {
      navigate('/dashboard');
    }
  };

  const handlePaymentError = (error: string) => {
    console.error('Payment error:', error);
    toast.error('Payment submission failed');
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);

    // Navigate to the dedicated payment completion page
    if (currentBooking && currentBooking.id) {
      navigate(`/bookings/${currentBooking.id}/payment`);
    }
  };

  const handleBookingSubmit = async (bookingData: BurialPlotBookingData, pricing: PricingResult) => {
    setIsLoading(true);
    try {
      // Navigate to plot selection with booking data
      const stateData = {
        burialData: bookingData,
        pricing: pricing,
        caseId: selectedCase
      };

      navigate('/bookings/burial-plot/select-plot', { state: stateData });
    } catch (error) {
      console.error('Navigation error:', error);
      toast.error('Failed to proceed to plot selection');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                Burial Plot Booking
              </h1>
              <p className="mt-2 text-sm text-neutral-600">
                Book a burial plot at verified cemeteries across Malaysia
              </p>
            </div>
          </div>
        </div>

        {/* Emergency Banner */}
        <div className="mb-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-red-900">
                  Peace of Mind Planning
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  Secure burial arrangements in advance or for immediate needs. All plots are verified and managed by trusted cemetery providers.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Case Selection (if not showing booking form) */}
        {!showBookingForm && (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
            <div className="p-6">
              <h2 className="text-lg font-medium text-neutral-900 mb-4">
                Start Your Booking
              </h2>
              <div className="space-y-4">
                {/* New Booking Option */}
                <button
                  onClick={handleNewBooking}
                  className="w-full text-left p-4 border-2 border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900">New Burial Arrangement</h3>
                      <p className="text-sm text-neutral-600">Book a burial plot without an existing case</p>
                    </div>
                  </div>
                </button>

                {/* Existing Cases */}
                {existingCases.length > 0 && (
                  <div>
                    <div className="border-t border-neutral-200 pt-4">
                      <h3 className="font-medium text-neutral-900 mb-3">
                        Or Book for Existing Case
                      </h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {existingCases.map((caseItem) => (
                          <button
                            key={caseItem.id}
                            onClick={() => handleCaseSelect(caseItem.id)}
                            className="w-full text-left p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-neutral-900">
                                  Case #{caseItem.caseNumber}
                                </h4>
                                <p className="text-sm text-neutral-600">
                                  {caseItem.deceasedName}
                                </p>
                              </div>
                              <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Booking Form */}
        {showBookingForm && (
          <BurialPlotBookingForm
            caseId={selectedCase || undefined}
            onSubmit={handleBookingSubmit}
            onCancel={() => setShowBookingForm(false)}
            disabled={isLoading}
          />
        )}

        {/* Payment Modal */}
        {showPaymentModal && currentBooking && currentPricing && (
          <BankTransferPayment
            isOpen={showPaymentModal}
            bookingId={currentBooking.id}
            amount={currentPricing.total}
            currency={currentPricing.currency || 'MYR'}
            transferReference={`EKB${Date.now()}`}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onCancel={handlePaymentCancel}
          />
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6">
              <LoadingSpinner />
              <p className="mt-4 text-neutral-600">Processing your booking...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BurialPlotBookingPage;